<!--
	@name app-firm-dialog-add-providers
	@description trading style add providers dialog component
	@date 2020/06/23
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div>
		<v-dialog v-model="active" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable>
			<v-card tile>
				<v-toolbar flat dark class="pl-3 pr-3 flex-grow-0 generic-gradient">
					<v-row>
						<v-col cols="12" md="2" class="d-flex pa-0">
							<v-toolbar-title class="align-self-center">
								<span v-if="panel">
									Edit Panel
								</span>
								<span v-else>
									Create Panel
								</span>
							</v-toolbar-title>
						</v-col>
						<v-spacer />
						<v-col cols="12" md="2" class="d-flex pa-0 justify-end">
							<v-btn icon dark class="align-self-center" @click="active = false">
								<v-icon>mdi-close</v-icon>
							</v-btn>
						</v-col>
					</v-row>
				</v-toolbar>
				<v-card-text>
					<v-container>
						<div v-if="advanced">
							<v-btn text color="primary" class="mb-6 ml-n3" @click="openList">
								<v-icon left>
									mdi-backburger
								</v-icon>Back to panel
							</v-btn>
							<p class="text-h6 font-weight-black mb-1">
								Advanced: Product restrictions by keyword
							</p>
							<p class="text-body-1 font-weight-bold warning--text mb-4">
								Provider: {{ advancedName }}
							</p>
							<p class="text-body-1 mb-3">
								Restrict products by keywords that appear in their titles.
							</p>

							<v-row>
								<v-col class="grow">
									<v-text-field v-model="newTerm" label="Restriction Keyword" hide-details="auto" />
								</v-col>
								<v-col class="shrink align-content-end">
									<v-btn color="primary" class="mb-0" :disabled="newTerm == ''" :loading="loading" @click="addTerm">
										Add keyword
									</v-btn>
								</v-col>
							</v-row>

							<div v-if="terms.length" class="mt-3">
								<v-row align="center" justify="start" class="my-n1 mx-n2" no-gutters>
									<v-chip v-for="term in terms" :key="term" :value="term" close class="my-1 mx-2" @click:close="removeTerm(term)">
										{{ term }}
									</v-chip>
								</v-row>
								<p class="mt-3 text-body-2 mb-0">
									<span class="text-uppercase blue--text">RESTRICT</span> all products <span class="text-uppercase blue--text">WHERE</span>
									<span v-for="(term, index) in terms" :key="index">
										<span v-if="index" class="text-uppercase blue--text"> AND</span>
										<span class="font-weight-bold"> Product Name contains "{{ term }}" </span>
									</span>
								</p>
							</div>

							<div class="mt-8">
								<v-btn color="error" @click="setProductRestrictionsCancel">
									Cancel
								</v-btn>
								<v-btn color="success" class="ml-3" :disabled="setProductRestrictionsDisabled" :loading="loading" @click="submit({ dialog: { title: 'Product restrictions by keyword', message: 'Press OK to confirm that you wish to add these product restrictions. Restrictions will be applied with immediate effect.' } })">
									set product restrictions for {{ advancedName }}
								</v-btn>
							</div>
						</div>

						<div v-else>
							<p class="text-h6 font-weight-black mb-1">
								Panel Manager
							</p>

							<div v-if="mode === 'list'">
								<v-row class="mb-10">
									<v-col class="grow py-0">
										<v-text-field v-model="panelName" label="Panel Name" hide-details="auto" />
									</v-col>
									<v-col class="shrink align-content-end py-0">
										<div v-if="panel" class="nowrap">
											<v-btn v-if="storedData.panelName && !panelNameButtonDisabled" class="mr-3" color="error" @click="cancelUpdatePanelName">
												Cancel
											</v-btn>
											<v-btn color="success" class="mb-0" :disabled="panelNameButtonDisabled" :loading="loading" @click="updatePanelName">
												change panel name
											</v-btn>
										</div>
										<v-btn v-else color="success" class="mb-0" :disabled="panelNameButtonDisabled" :loading="loading" @click="addPanelName">
											add panel name
										</v-btn>
									</v-col>
								</v-row>
								<div v-if="panel && organisations.length">
									<div v-if="selectedProviders.length">
										<p class="text-body-2 mb-4">
											Build and manage bespoke panels to manage your firm's provider access.
										</p>
										<v-btn color="primary" class="mb-4" @click="openEdit">
											add / remove providers
										</v-btn>
										<v-data-table
											class="table--styled elevation-3"
											:headers="headers"
											hide-default-footer
											disable-pagination
											:items="selectedProviders"
										>
											<template #body="{ items }">
												<tbody>
													<tr v-for="item in items" :key="item.value" class="tbl-row">
														<td>
															<p class="text-body-1 font-weight-bold mb-0">
																{{ item.text }}
															</p>
															<p v-if="panel?.data?.reject?.[item.value] && panel?.data?.reject?.[item.value].length" class="text-body-2 warning--text font-weight-medium mb-0">
																<v-icon color="warning" size="16" class="mr-2" style="position:relative; top:-1px">
																	mdi-alert
																</v-icon>Advanced Configuration
															</p>
															<p v-else class="text-body-2 font-weight-regular grey--text mb-0">
																All products
															</p>
														</td>
														<td class="text-right shrunk">
															<v-tooltip bottom content-class="text-center" max-width="250">
																<template #activator="{ on }">
																	<v-btn :disabled="!organisation.data?.panels?.advanced" color="primary" small icon @click="openAdvanced(item.value)" v-on="on">
																		<v-icon>mdi-cog</v-icon>
																	</v-btn>
																</template>
																<span> Advanced panel configuration </span>
															</v-tooltip>
														</td>
													</tr>
												</tbody>
											</template>
										</v-data-table>
									</div>
									<div v-else-if="panel && !organisations.length">
										<v-skeleton-loader type="card" />
									</div>
									<div v-else>
										<p class="text-body-2 font-weight-bold mb-3">
											Your panel is currently empty.
										</p>
										<v-btn color="primary" @click="openEdit">
											Add your first panel provider
										</v-btn>
									</div>
								</div>
							</div>

							<div v-else-if="mode === 'edit'">
								<div v-if="selectedProviders.length">
									<p class="text-body-2 font-weight-bold mb-3">
										Your panel currently consists of {{ selectedProviders.length === 1 ? `one provider` : `these ${selectedProviders.length} providers` }}:
									</p>
									<v-row align="center" justify="start" class="my-n1 mx-n2" no-gutters>
										<v-chip
											v-for="(selection, i) in selectedProviders"
											:key="selection.text"
											:disabled="loading"
											close
											class="my-1 mx-2"
											@click:close="selectedProviders.splice(i, 1)"
										>
											{{ selection.text }}
										</v-chip>
									</v-row>
								</div>

								<v-card-text class="scrollbar pa-0 mt-6">
									<v-list class="py-0 my-0" :max-height="300">
										<template v-for="item in providerOptions">
											<v-list-item v-if="!selectedProviders.includes(item)" :key="item.text" :disabled="loading" @click="selectedProviders.push(item)">
												<v-list-item-title v-text="item.text" />
											</v-list-item>
										</template>
									</v-list>
								</v-card-text>

								<div class="mt-6">
									<v-btn color="error" @click="cancelUpdateProviders">
										Cancel
									</v-btn>
									<v-btn color="success" class="ml-3" :loading="loading" :disabled="confirmPanelSelectionDisabled" @click="submit({ dialog: { title: 'Panel Manager', message: 'Press OK to confirm that you wish to make these panel changes. Changes will be applied with immediate effect.' } })">
										Confirm panel selection
									</v-btn>
								</div>
							</div>
						</div>
					</v-container>
				</v-card-text>
			</v-card>
		</v-dialog>
		<common-dialog-confirm ref="confirm" />
	</div>
</template>

<script>
	import CommonDialogConfirm from '@/component/common/dialog/confirm';
	import { mapActions, mapState } from 'vuex';
	import { ElementTools } from '@/utils';
	import Vue from 'vue';

	export default {
		name: 'app-firm-dialog-add-providers',

		components: {
			CommonDialogConfirm
		},

		data() {
			return {
				storedData: {},
				active: false,
				mode: 'list',
				advanced: null,
				newTerm: '',
				terms: [],
				panel: null,
				selectedProviders: [],
				loading: false,
				panelName: null,
				disableAddPanelButton: false,
				organisations: []
			};
		},

		computed: {
			...mapState('Account', ['organisation']),

			headers() {
				return [
					{ text: 'Name', align: 'left', value: 'name', class: 'text-no-wrap', sortable: false },
					{ text: '', align: 'centre', sortable: false }
				];
			},

			advancedName() {
				if (!this.selectedProviders.length) return null;
				let item = this.selectedProviders.find((x) => x.value === this.advanced);
				if (!item) return null;
				return item.text;
			},

			providerOptions() {
				return this.organisations
					?.filter((o) => !this.selectedProviders.find((x) => x.value === o.id))
					?.map((o) => ({
						text: o.name,
						value: o.id
					}));
			},

			panelNameButtonDisabled() {
				if(this.disableAddPanelButton) return true;
				if(this.panelName == null || this.panelName == "") return true;
				if(this.storedData.panelName && this.storedData.panelName == this.panelName) return true;
				return false;
			},

			confirmPanelSelectionDisabled() {
				if(!this.selectedProviders.length) return true;
				if(this.storedData.selectedProviders && JSON.stringify(this.storedData.selectedProviders) == JSON.stringify(this.selectedProviders)) return true;
				return false;
			},

			setProductRestrictionsDisabled() {
				if(!('terms' in this.storedData) && !this.terms.length) return true; // terms not saved, list empty
				if(!this.storedData.terms.length && !this.terms.length) return true; // terms saved as empty, list empy
				if(this.storedData.terms && JSON.stringify(this.storedData.terms) == JSON.stringify(this.terms)) return true; // terms saved and same as current list
				return false; // button is active
			}
		},

		methods: {
			...mapActions('AccountPanel', ['addPanel', 'modifyPanel']),
			...mapActions('AppMatterType', ['loadMatterTypeList']),

			/**
			 * @name open
			 * @description open providers dialog
			 */
			async open(panel) {
				this.openList();

				// First get organisations
				this.loadMatterTypeList({ with: 'organisation', order: { property: 'position' } }).then(({ data }) => {
					const matterType = data.find((matterType) => matterType.nameUnique === 'lifetime_mortgage');
					this.organisations = matterType.organisation.sort((a, b) => a.name.localeCompare(b.name));


					if(panel) {
						this.panel = panel;
						this.panelName = panel.name;
						Object.assign(this.selectedProviders, this.organisations);
						this.selectedProviders = this.selectedProviders
						?.filter((o) => (this.panel.data.panel?.sourcing || []).includes(o.id))
						?.map((o) => ({
							text: o.name,
							value: o.id
						}));
					} else {
						this.panel = null;
						this.panelName = null;
						this.selectedProviders = [];
					}
					this.loading = false;
					this.active = true;

					this.setStoredData('panelName');
					this.$emit('panel-opened', panel ?? `add`);
				});
			},

			setStoredData(data) {
				let value = null;
				if(typeof data === 'string' || data instanceof String) {
					value = this[data];
					if(typeof value === 'object') {
						if(Array.isArray(value)) value = [ ...this[data] ];
						else value = { ...this[data] };
					}
					Vue.set(this.storedData, data, value);
				} else {
					Object.keys(data).forEach((key) => {
						value = this[key];
						if(typeof value === 'object') value = { ...this[key] };
						Vue.set(this.storedData, key, value);
					});
				}
			},

			openList() {
				this.advanced = null;
				this.mode = 'list';
			},

			openEdit() {
				this.advanced = null;
				this.mode = 'edit';
				this.setStoredData('selectedProviders');
			},

			openAdvanced(id) {
				this.mode = null;
				this.newTerm = '',
				this.terms = this.panel?.data?.reject?.[id] ? [...this.panel?.data?.reject?.[id]] : [];
				this.advanced = id;
				this.setStoredData('terms');
			},

			setProductRestrictionsCancel() {
				this.terms = this.storedData.terms;
				this.openList();
			},

			cancelUpdateProviders() {
				this.selectedProviders = this.storedData.selectedProviders;
				this.openList();
			},

			/**
			 * @name submit
			 * @description submit new providers
			 */
			submit(options) {
				this.$refs.confirm
					.open(options.dialog?.title || 'Confirm', options.dialog?.message || `Are you sure?`)
					.then(() => {
						this.loading = true;

						// Array of provider UUIDs
						const sourcing = this.selectedProviders.map((p) => p.value);

						// Filter reject data sets by provider - only providers in list above apply
						const reject = this.panel.data?.reject ? [...Object.keys(this.panel.data.reject), this.advanced]
							.filter(key => sourcing.includes(key))
							.reduce((obj, key) => {
								if(this.advanced && this.advanced === key) {
									obj[key] = this.terms;
								} else {
									obj[key] = this.panel.data.reject[key];
								}
								return obj;
							}, {}) : {
								[this.advanced]: this.terms
							};

							this.modifyPanel({
									id: this.panel.id,
									payload: {
										sourcing: this.selectedProviders.map((p) => p.value),
										reject: reject
									}
								})
							.then((panel) => {
								this.open(panel);
								ElementTools.fireNotification(this.$el, 'success', 'Providers have been added successfully');
							})
							.catch(() => ElementTools.fireNotification(this.$el, 'error', 'An error occured whilst adding Providers to this enquiry'))
							.finally(() => {
								this.loading = false;
								this.$emit('submitted');
								this.setStoredData('selectedProviders');
								this.setStoredData('terms');
							});
					})
					.catch(() => {});
			},

			addPanelName() {
				this.disableAddPanelButton = true;
				const payload = {
					name: this.panelName
				};
				this.addPanel(payload)
					.then((panel) => {
						this.open(panel);
						this.setStoredData('panelName');
						ElementTools.fireNotification(this.$el, 'success', 'Panel name has been updated successfully');
					})
					.catch((e) => {
						console.log(e);
						ElementTools.fireNotification(this.$el, 'error', 'An error occured whilst updating the panel name');
					})
					.finally(() => {
						this.disableAddPanelButton = false;
					});
			},

			cancelUpdatePanelName() {
				this.panelName = this.storedData.panelName;
			},

			updatePanelName() {
				const payload = {
					name: this.panelName,
					sourcing: this.panel?.data?.panel?.sourcing ?? [],
					reject: this.panel?.data?.reject ?? {}
				};
				this.modifyPanel({ id: this.panel.id, payload})
					.then((panel) => {
						this.open(panel);
						this.setStoredData('panelName');
						ElementTools.fireNotification(this.$el, 'success', 'Panel name has been updated successfully');
					})
					.catch(() => ElementTools.fireNotification(this.$el, 'error', 'An error occured whilst updating the panel name'));
			},

			/**
			 * @name addTerm
			 * @description add restriction term to provider
			 */
			 addTerm() {
				if(this.newTerm) {
					this.terms.push(this.newTerm);
					this.newTerm = '';
				}
			},

			/**
			 * @name removedTerm
			 * @description add restriction term to provider
			 */
			 removeTerm(string) {
				const index = this.terms.indexOf(string);
				if (index !== -1) {
					this.terms.splice(index, 1);
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	::v-deep .scrollbar {
		overflow-y: scroll;
		border: 1px solid map-get($grey, lighten-1);
	}
</style>